/**
 * PageWrapper - Premium 2026 Page Layout Component
 * 
 * Ultra-modern page structure with:
 * - Animated gradient headers
 * - Glassmorphism breadcrumbs
 * - Smooth page transitions
 * - Premium typography
 * 
 * @component
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Breadcrumbs,
    Link,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Home as HomeIcon,
    Refresh as RefreshIcon,
    ArrowBack as BackIcon,
    ChevronRight as ChevronIcon
} from '@mui/icons-material';

/**
 * PageWrapper Component - Premium 2026 Edition
 */
const PageWrapper = ({
    title,
    subtitle,
    breadcrumbs = [],
    actions,
    onRefresh,
    showBack = false,
    children,
    headerGradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
}) => {
    const navigate = useNavigate();

    return (
        <div>
            <Box sx={{
                minHeight: '100vh',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '400px',
                    background: 'radial-gradient(ellipse at top, rgba(102, 126, 234, 0.08) 0%, transparent 60%)',
                    pointerEvents: 'none',
                    zIndex: 0
                }
            }}>
                {/* Premium Header Section */}
                <Box sx={{
                    position: 'relative',
                    zIndex: 1,
                    p: { xs: 2, md: 4 },
                    pb: { xs: 1, md: 2 }
                }}>
                    {/* Breadcrumbs - Glassmorphism Style */}
                    {breadcrumbs.length > 0 && (
                        <div>
                            <Breadcrumbs
                                separator={<ChevronIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                                sx={{
                                    mb: 3,
                                    '& .MuiBreadcrumbs-ol': {
                                        background: 'rgba(255, 255, 255, 0.7)',
                                        backdropFilter: 'blur(12px)',
                                        WebkitBackdropFilter: 'blur(12px)',
                                        borderRadius: 3,
                                        px: 2,
                                        py: 1,
                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
                                        display: 'inline-flex'
                                    }
                                }}
                            >
                                <Link
                                    underline="none"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        transition: 'color 0.2s ease',
                                        '&:hover': {
                                            color: 'primary.main'
                                        }
                                    }}
                                    onClick={() => navigate('/admin')}
                                >
                                    <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                    Home
                                </Link>
                                {breadcrumbs.map((crumb, index) => {
                                    const isLast = index === breadcrumbs.length - 1;
                                    return isLast ? (
                                        <Typography
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                color: 'primary.main'
                                            }}
                                        >
                                            {crumb.icon && <crumb.icon sx={{ mr: 0.5, fontSize: 18 }} />}
                                            {crumb.label}
                                        </Typography>
                                    ) : (
                                        <Link
                                            key={index}
                                            underline="none"
                                            sx={{
                                                cursor: 'pointer',
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                                fontSize: '0.875rem',
                                                transition: 'color 0.2s ease',
                                                '&:hover': {
                                                    color: 'primary.main'
                                                }
                                            }}
                                            onClick={() => navigate(crumb.path)}
                                        >
                                            {crumb.label}
                                        </Link>
                                    );
                                })}
                            </Breadcrumbs>
                        </div>
                    )}

                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2,
                        mb: 4
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {showBack && (
                                <div>
                                    <IconButton
                                        onClick={() => navigate(-1)}
                                        sx={{
                                            background: 'rgba(255, 255, 255, 0.8)',
                                            backdropFilter: 'blur(8px)',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                            '&:hover': {
                                                background: 'rgba(255, 255, 255, 0.95)'
                                            }
                                        }}
                                    >
                                        <BackIcon />
                                    </IconButton>
                                </div>
                            )}
                            <Box>
                                <div>
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontWeight: 800,
                                            background: headerGradient,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            letterSpacing: '-0.02em',
                                            lineHeight: 1.2,
                                            mb: subtitle ? 0.5 : 0
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                </div>
                                {subtitle && (
                                    <div>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                                maxWidth: 600
                                            }}
                                        >
                                            {subtitle}
                                        </Typography>
                                    </div>
                                )}
                            </Box>
                        </Box>

                        <div>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                {onRefresh && (
                                    <div>
                                        <Tooltip title="Refresh" arrow>
                                            <IconButton
                                                onClick={onRefresh}
                                                sx={{
                                                    background: 'rgba(102, 126, 234, 0.1)',
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        background: 'rgba(102, 126, 234, 0.2)'
                                                    }
                                                }}
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                )}
                                {actions}
                            </Box>
                        </div>
                    </Box>
                </Box>

                {/* Content */}
                <Box sx={{
                    position: 'relative',
                    zIndex: 1,
                    px: { xs: 2, md: 4 },
                    pb: 4
                }}>
                    <div>
                        {children}
                    </div>
                </Box>
            </Box>
        </div>
    );
};

export default React.memo(PageWrapper);
